import React, { useEffect } from 'react';

const CartModal = ({ isOpen, onClose, cartItems, onRemoveItem }) => {
    // 1. Lock the body scroll so the background doesn't move when the cart is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    if (!isOpen) return null;

    const totalPrice = cartItems.reduce((acc, item) => {
        const itemPrice = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
        return acc + itemPrice * item.quantity;
    }, 0);

    return (
        /* 2. OVERLAY: fixed position to keep it in the viewport regardless of page scroll.
           Items-center and justify-center ensures it's perfectly centered.
        */
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-300">
            {/* Backdrop click area */}
            <div className="absolute inset-0 z-[-1]" onClick={onClose}></div>

            {/* 3. THE MODAL: max-h-[85vh] ensures it floats in the middle as a centered box.
               flex flex-col allows the body to scroll while header and footer stay pinned.
            */}
            <div
                className="bg-white rounded-3xl shadow-2xl w-[calc(100%-2rem)] max-w-md relative flex flex-col max-h-[85vh] overflow-hidden animate-slide-up mx-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER: Pinned at the top */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100 shrink-0">
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <svg className="w-7 h-7 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Your Cart
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* BODY: Scrollable content area */}
                <div className="overflow-y-auto p-6 flex-1 custom-scrollbar bg-white">
                    {cartItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center py-10">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                            </div>
                            <p className="text-gray-400 text-lg font-medium">Your cart is empty.</p>
                            <button onClick={onClose} className="mt-4 text-amber-600 font-bold hover:underline">Start Shopping</button>
                        </div>
                    ) : (
                        <ul className="space-y-6">
                            {cartItems.map((item, idx) => {
                                const itemPrice = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
                                return (
                                    <li key={idx} className="flex gap-4 items-center pb-6 border-b border-gray-50 last:border-0">
                                        <div className="w-20 h-20 shrink-0 bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                                            <img
                                                src={item.image || 'https://via.placeholder.com/150'}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-gray-800 text-base truncate">{item.name}</h3>
                                            <p className="text-sm text-gray-500 mt-1">Qty: <span className="font-bold text-gray-700">{item.quantity}</span></p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="font-bold text-gray-900">{(itemPrice * item.quantity).toFixed(2)} ETB</p>
                                            <button
                                                onClick={() => onRemoveItem(idx)}
                                                className="text-xs text-red-500 hover:text-red-700 mt-2 font-medium underline"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* FOOTER: Fixed at the bottom */}
                {cartItems.length > 0 && (
                    <div className="border-t border-gray-100 p-6 bg-gray-50 shrink-0">
                        <div className="flex justify-between items-center mb-6">
                            <span className="text-gray-500 font-medium">Subtotal</span>
                            <span className="text-3xl font-black text-gray-900 tracking-tighter">
                                {totalPrice.toFixed(2)} ETB
                            </span>
                        </div>
                        <button
                            className="w-full py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl font-bold text-lg transition-all active:scale-[0.99] shadow-xl shadow-amber-600/20"
                            onClick={() => alert(`Checkout not implemented yet!`)}
                        >
                            Checkout Now
                        </button>
                    </div>
                )}
            </div>

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
                
                @keyframes slide-up {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                .animate-slide-up {
                    animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
            `}</style>
        </div>
    );
};

export default CartModal;